/**
 * Account Service Layer - 业务逻辑层 + Mock 适配
 */

import { config } from '../../config';
import * as accountMock from '../../mocks/account';
import type { ApiResponse, PageData } from '../../types/common';
import type {
  Account,
  PageAccountRequest,
  PageAccountData,
  CreateAccountRequest,
  UpdateAccountRequest,
} from '../../types/account';
import httpClient from '../httpClient';
import { API_ENDPOINTS } from '../../utils/apiEndpoints';

/**
 * 分页查询账号 (符合 domain-frontend-spec 规范)
 */
export async function pageAccounts(
  params: PageAccountRequest
): Promise<ApiResponse<PageAccountData>> {
  if (config.mock) {
    return accountMock.mockPageAccounts(params);
  }

  return httpClient.post(API_ENDPOINTS.ACCOUNT.PAGE, params);
}

/**
 * 创建账号
 */
export async function createAccount(data: CreateAccountRequest): Promise<ApiResponse<Account>> {
  if (config.mock) {
    return accountMock.mockCreateAccount(data);
  }

  return httpClient.post(API_ENDPOINTS.ACCOUNT.CREATE, data);
}

/**
 * 更新账号
 */
export async function updateAccount(data: UpdateAccountRequest): Promise<ApiResponse<Account>> {
  if (config.mock) {
    return accountMock.mockUpdateAccount(data);
  }

  return httpClient.post(API_ENDPOINTS.ACCOUNT.UPDATE, data);
}

/**
 * 删除账号
 */
export async function deleteAccount(accountId: number): Promise<ApiResponse<void>> {
  if (config.mock) {
    return accountMock.mockDeleteAccount(accountId);
  }

  return httpClient.post(API_ENDPOINTS.ACCOUNT.DELETE, { loginId: accountId });
}

/**
 * 修改账号密码
 */
export async function changeAccountPassword(data: {
  accountId: number;
  newPassword: string;
}): Promise<ApiResponse<void>> {
  if (config.mock) {
    // Mock 模式下直接返回成功
    return Promise.resolve({
      success: true,
      errorMessage: null,
      flag: 'success',
      data: null,
      traceId: `mock-${Date.now()}`,
    });
  }

  return httpClient.post(API_ENDPOINTS.ACCOUNT.CHANGE_PASSWORD, data);
}

/**
 * 获取账号的角色列表
 */
export async function getAccountRoles(accountId: number): Promise<ApiResponse<any>> {
  if (config.mock) {
    // Mock 模式下返回空列表
    return Promise.resolve({
      success: true,
      errorMessage: null,
      flag: 'success',
      data: [],
      traceId: `mock-${Date.now()}`,
    });
  }

  return httpClient.get(`${API_ENDPOINTS.ACCOUNT.GET_ROLES}?accountId=${accountId}`);
}

/**
 * 分配角色给账号
 */
export async function assignRoles(data: {
  accountId?: number;
  loginId?: string;
  roleCodeSet: string[];
}): Promise<ApiResponse<void>> {
  if (config.mock) {
    return accountMock.mockAssignRoles({
      loginId: data.loginId || '',
      roleCodes: data.roleCodeSet,
    });
  }

  return httpClient.post(API_ENDPOINTS.ACCOUNT.ASSIGN_ROLES, data);
}

/**
 * 移除账号的角色
 */
export async function removeRoles(data: {
  accountId?: number;
  loginId?: string;
  roleCodes?: string[];
  removeRoleCodeSet?: string[];
}): Promise<ApiResponse<void>> {
  if (config.mock) {
    return accountMock.mockRemoveRoles({
      loginId: data.loginId || '',
      roleCodes: data.roleCodes,
      removeRoleCodeSet: data.removeRoleCodeSet,
    });
  }

  return httpClient.post(API_ENDPOINTS.ACCOUNT.REMOVE_ROLES, data);
}

/**
 * 审批通过账号申请（按临时员工 loginId 定位最新 ACCOUNT_CREATE 申请）
 */
export async function approveAccount(data: {
  loginId: string;
  expiryDate: string | null;
}): Promise<ApiResponse<Account>> {
  if (config.mock) {
    return accountMock.mockApproveAccount(data);
  }

  return httpClient.post(API_ENDPOINTS.ACCOUNT.APPROVE, {
    loginId: data.loginId,
    action: 'approve',
    expiryDate: data.expiryDate,
  });
}

/**
 * 拒绝账号申请（按临时员工 loginId 定位最新 ACCOUNT_CREATE 申请）
 */
export async function rejectAccount(data: {
  loginId: string;
  rejectReason: string;
}): Promise<ApiResponse<Account>> {
  if (config.mock) {
    return accountMock.mockRejectAccount(data);
  }

  return httpClient.post(API_ENDPOINTS.ACCOUNT.REJECT, {
    loginId: data.loginId,
    action: 'reject',
    remark: data.rejectReason,
  });
}

/**
 * 批量启用账号
 */
export async function enableAccounts(accountIds: number[]): Promise<ApiResponse<void>> {
  if (config.mock) {
    return accountMock.mockEnableAccounts(accountIds);
  }

  return httpClient.post(API_ENDPOINTS.ACCOUNT.ENABLE, { accountIds });
}

/**
 * 批量停用账号
 */
export async function disableAccounts(accountIds: number[]): Promise<ApiResponse<void>> {
  if (config.mock) {
    return accountMock.mockDisableAccounts(accountIds);
  }

  return httpClient.post(API_ENDPOINTS.ACCOUNT.DISABLE, { accountIds });
}

/**
 * 更新账号熟练度等级
 */
export async function updateProficiency(employeeId: string, newLevel: string): Promise<ApiResponse<void>> {
  return httpClient.post(API_ENDPOINTS.ACCOUNT.UPDATE_PROFICIENCY, { employeeId, newLevel });
}

/**
 * 查询当前门店下的账号列表（支持 nameLike 模糊查询）
 */
export async function queryStoreAccounts(params: {
  currentPage: number;
  pageSize: number;
  nameLike?: string;
  loginIdLike?: string;
}): Promise<ApiResponse<PageData<{ loginId: string; name: string }>>> {
  if (config.mock) {
    return Promise.resolve({
      success: true,
      errorMessage: null,
      flag: 'success',
      data: { tableData: [], paging: { currentPage: 1, pageSize: 20, totalCount: 0 } },
      traceId: `mock-${Date.now()}`,
    });
  }
  return httpClient.post(API_ENDPOINTS.ACCOUNT.STORE_ACCOUNTS, params);
}
